/*
 * Extracted from WalletPage, which had grown to 920 lines around four
 * module-level components. They were files in everything but name: each has
 * its own state, its own Stripe calls and its own error surface, and none of
 * them referenced the others' internals.
 */
import { useEffect, useState } from 'react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { SetupIntent } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { useNotifications } from '@/app/providers/notifications';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { createSetupIntent, deletePaymentMethod } from '@/features/wallet/services/walletService';
import { safeErrorMessage, stripeErrorMessage } from '@/shared/utils/errors';
import type { PaymentMethodSummary } from '@/shared/types';
import { hasStripeKey, stripePromise } from '@/features/wallet/services/stripeClient';
import { Button, Card, CardHeader, EmptyState, TrashIcon } from '@/shared/ui';
import { poll } from '@/features/wallet/utils/poll';

// Stripe Elements renders inside an iframe, so it can't read our CSS variables —
// these values mirror the app's ink/muted tokens closely enough.
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontFamily: 'inherit',
      fontSize: '15px',
      color: '#243b32',
      '::placeholder': { color: '#9aa9a0' },
    },
    invalid: { color: '#c0392b' },
  },
};
// Card save polls longer than the top-up path. Billing only learns a card exists when
// Stripe delivers setup_intent.succeeded, measured locally at ~2s end to end; 8 attempts
// spans ~5.6s, so an unusually slow webhook still resolves on-screen rather than leaving
// the user reading "shortly" next to the digits of the card they just replaced.
const CARD_SAVE_POLL_ATTEMPTS = 8;

// The PaymentMethod id Stripe just confirmed. `payment_method` comes back as a bare id
// on a plain confirmation and as an expanded object when the intent was retrieved with
// expansion — accept both rather than banking on the shape we happen to get today.
function confirmedPaymentMethodId(setupIntent: SetupIntent | null | undefined): string | null {
  const pm = setupIntent?.payment_method;
  if (typeof pm === 'string') return pm;
  return pm?.id ?? null;
}

// Has the summary caught up to the card we just saved, or is it still showing the one
// that card replaced? This is what the post-save poll waits on.
//
// "A card exists" is not good enough, and that was the bug: when the user is UPDATING a
// card, one already exists, so that test passed on the very first read and the poll
// returned the OLD card — which the page then displayed while announcing "Card saved".
//
// Identity is the right comparison because every save mints a NEW PaymentMethod even for
// identical digits, so re-saving the same card is still detected.
//
// Both ids are null against a billing/engine deploy predating payment-method ids, so
// fall back to "the visible card changed". That is exact for the reported case
// (3155 -> 4242) and merely conservative for a same-card re-save: the poll runs out and
// the user gets the honest "will appear here shortly" rather than a false confirmation.
//
// `replacedKnown` is false when the pre-save card could not be read at all (the
// wallet load failed and the retry at save time failed too). A null `replaced`
// then means "unknown", not "no card on file" — and treating unknown as
// first-ever is how this once accepted the OLD card as the new one. Without a
// baseline and without ids there is nothing to compare, so refuse to confirm
// and let the poll run out into the honest wording.
function reflectsSavedCard(
  pm: PaymentMethodSummary | null | undefined,
  savedId: string | null,
  replaced: PaymentMethodSummary | null,
  replacedKnown: boolean,
): boolean {
  if (!pm) return false;
  if (savedId && pm.id) return pm.id === savedId;
  if (!replacedKnown) return false;
  if (!replaced) return true; // first-ever card — nothing it could be confused with
  return pm.brand !== replaced.brand || pm.last4 !== replaced.last4;
}

export function WalletCardSection({
  paymentMethod,
  refreshPaymentMethod,
}: {
  paymentMethod: PaymentMethodSummary | null;
  refreshPaymentMethod: () => Promise<PaymentMethodSummary | null>;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { pushNotification } = useNotifications();
  const [formOpen, setFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  // Detect Stripe.js failing to load (offline / adblock / CSP) so we can say so,
  // instead of leaving the form stuck on "still loading" forever.
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);
  // Remove-card flow: a two-step confirm before detaching (no browser confirm()).
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  useEffect(() => {
    if (!hasStripeKey) return;
    let active = true;
    stripePromise
      .then((loaded) => {
        if (active && loaded === null) setStripeLoadFailed(true);
      })
      .catch(() => {
        if (active) setStripeLoadFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const stripeUnavailable = !hasStripeKey || stripeLoadFailed;
  const stripeReady = Boolean(stripe && elements);

  const closeForm = () => {
    setFormOpen(false);
    setCardError(null);
  };

  const handleSaveCard = async () => {
    setCardError(null);
    if (!stripe || !elements) {
      setCardError(
        stripeUnavailable
          ? 'Card payments are unavailable right now. Please try again later.'
          : 'The payment form is still loading — give it a second and try again.',
      );
      return;
    }
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setCardError('Enter your card details first.');
      return;
    }
    // The card this save replaces, read before anything can refresh it — null on a
    // first-ever save. reflectsSavedCard needs it to tell the two apart. A null
    // prop is ambiguous (no card, or the load failed?), so resolve it with one
    // fresh read now, before the charge: success — even a null one — is an
    // authoritative answer, and only a failure leaves the baseline unknown.
    let replaced = paymentMethod;
    let replacedKnown = replaced !== null;
    setIsSaving(true);
    try {
      if (!replacedKnown) {
        try {
          replaced = await refreshPaymentMethod();
          replacedKnown = true;
        } catch {
          // Still unknown; reflectsSavedCard refuses the no-card shortcut.
        }
      }
      const clientSecret = await createSetupIntent();
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });
      if (error) {
        // Card/validation messages are Stripe's own user-ready copy (incomplete
        // number, expired card, wrong CVC, declined); other types render catalog copy.
        setCardError(stripeErrorMessage(error, "We couldn't save that card. Check the details and try again."));
        return;
      }
      const savedId = confirmedPaymentMethodId(setupIntent);
      const settled = (pm: PaymentMethodSummary | null | undefined) =>
        reflectsSavedCard(pm, savedId, replaced, replacedKnown);
      if (setupIntent?.status === 'succeeded') {
        // The card is persisted asynchronously by billing's setup_intent.succeeded
        // webhook — poll so the summary reflects it (or fall back to "shortly").
        const saved = await poll(refreshPaymentMethod, settled, CARD_SAVE_POLL_ATTEMPTS);
        // Re-check rather than testing `saved` for null: poll returns the last value it
        // saw even when unsatisfied, and on an update that value is the OLD card — which
        // is how this came to announce "Card saved" over the previous card's digits.
        pushNotification({
          message: settled(saved) ? 'Card saved' : 'Card saved — it will appear here shortly.',
        });
        closeForm();
      } else if (setupIntent?.status === 'processing') {
        pushNotification({ message: 'Card is being verified — it will appear here shortly.' });
        void poll(refreshPaymentMethod, settled, CARD_SAVE_POLL_ATTEMPTS);
        closeForm();
      } else {
        setCardError("Card setup didn't complete. Please try again.");
      }
    } catch (err) {
      setCardError(safeErrorMessage(err, "We couldn't save that card. Please try again."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveCard = async () => {
    setRemoveError(null);
    setIsRemoving(true);
    try {
      /* Two operations, two try blocks, and the split is the point. They shared
         one before, so a transient failure re-READING the card was reported as
         a failure to REMOVE it — "We couldn't remove that card. Please try
         again." over a card billing had already detached. The user then either
         retries a delete that has nothing left to delete, or believes a card
         they no longer have is still on file. Both are worse than saying
         nothing.

         The save path below has always wrapped its own re-read in `poll()` for
         exactly this reason; this was the one place the asymmetry had not been
         noticed. */
      await deletePaymentMethod();
    } catch (err) {
      setRemoveError(safeErrorMessage(err, "We couldn't remove that card. Please try again."));
      setIsRemoving(false);
      return;
    }

    // Past here the card IS gone, so nothing below may surface as a removal
    // failure. Detach clears billing's cached card; reflect the empty state
    // immediately, and if that read fails, let the next load reconcile it.
    try {
      await refreshPaymentMethod();
    } catch (error: unknown) {
      /* Swallowed for the user, reported for whoever has to explain it later.
         The card IS gone, so this must not surface as a removal failure — but
         with nothing recorded, a stale card display has no trace back to the
         read that failed.

         `Sentry.captureException` rather than a `console.warn`, following
         `useLogoutRedirect`, which is the same shape: an error the user must
         not see and a developer must. The house `logger` is debug-gated and
         compiles out of production, so it would report exactly nothing on the
         one build where this matters, and a raw console call would be the only
         one in `src/`. */
      Sentry.captureException(error, { tags: { action: 'refresh-payment-method-after-delete' } });
    }
    pushNotification({ message: 'Card removed' });
    setConfirmRemove(false);
    setIsRemoving(false);
  };

  return (
    <Card padding="lg">
      <CardHeader title="Payment method" headingLevel={2} />

      {paymentMethod ? (
        <>
          <div className="wallet-card-summary">
            <div className="wallet-card-summary__info">
              <div className="wallet-card-summary__brand">
                {paymentMethod.brand ?? 'Card'} •••• {paymentMethod.last4 ?? '••••'}
              </div>
              <div className="wallet-card-summary__meta">
                {paymentMethod.isDefault ? 'Default payment method' : 'Saved card'}
              </div>
            </div>
            <button
              type="button"
              className="wallet-card-remove"
              aria-label="Remove card"
              title="Remove card"
              onClick={() => {
                setRemoveError(null);
                setConfirmRemove(true);
              }}
              disabled={isRemoving || formOpen}
            >
              <TrashIcon size={16} />
            </button>
          </div>
          {confirmRemove && !formOpen && (
            <div className="wallet-card-confirm">
              <span className="wallet-card-confirm__text">Remove this card?</span>
              <div className="wallet-card-actions">
                <Button
                  variant="danger"
                  className="wallet-card-cta"
                  onClick={handleRemoveCard}
                  loading={isRemoving}
                >
                  {isRemoving ? 'Removing…' : 'Remove'}
                </Button>
                <Button onClick={() => setConfirmRemove(false)} disabled={isRemoving}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {removeError && <ErrorBanner message={removeError} onDismiss={() => setRemoveError(null)} />}
        </>
      ) : (
        !formOpen && (
          <EmptyState title="No card on file" hint="Add a card to enable top-ups." />
        )
      )}

      {stripeUnavailable && (
        <p className="wallet-note">
          {hasStripeKey
            ? 'The payment form failed to load. Check your connection (and any ad/script blockers), then refresh.'
            : 'Card payments are not configured for this environment.'}
        </p>
      )}

      {formOpen ? (
        <>
          <div className="wallet-card-element">
            <CardElement options={CARD_ELEMENT_OPTIONS} />
          </div>
          {cardError && <ErrorBanner message={cardError} onDismiss={() => setCardError(null)} />}
          <div className="wallet-card-actions">
            <Button
              variant="primary"
              className="wallet-card-cta"
              onClick={handleSaveCard}
              loading={isSaving}
              disabled={!stripeReady}
            >
              {isSaving ? 'Saving…' : 'Save card'}
            </Button>
            <Button onClick={closeForm} disabled={isSaving}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <Button
          className="wallet-card-cta"
          variant={paymentMethod ? 'secondary' : 'primary'}
          onClick={() => {
            setConfirmRemove(false);
            setFormOpen(true);
          }}
          disabled={stripeUnavailable}
        >
          {paymentMethod ? 'Update card' : 'Add a card'}
        </Button>
      )}
    </Card>
  );
}
